import { z } from 'zod';

import { issuedQuoteSchema } from './artifacts.ts';
import { DomainError } from './errors.ts';
import {
  CHECKOUT_SESSION_LIFETIME_MS,
  PAYMENT_PROVIDERS,
  type PaymentProblemKind,
  UNOPENED_ATTEMPT_GRACE_MS,
} from './payments.ts';

export const PURCHASE_STATES = ['pending', 'paid', 'cancelled', 'expired'] as const;
export const purchaseStateSchema = z.enum(PURCHASE_STATES);
export type PurchaseState = z.infer<typeof purchaseStateSchema>;

/**
 * Where an issued quote's panel cost came from: Remesh's own price for the study's inputs, or the
 * same arithmetic worked out here in an environment with no Remesh, which can never field what it
 * prices.
 */
export const PANEL_COST_SOURCES = ['remesh', 'offline'] as const;
export type PanelCostSource = (typeof PANEL_COST_SOURCES)[number];

const purchasedArtifactSchema = z.strictObject({
  artifactId: z.uuid(),
  version: z.int().positive(),
});
/**
 * The versions a purchase holds the study at, each approved at current under the study row lock:
 * the research questions, the guide and the audience; the chosen Goal (its current version, the one
 * the research questions decompose); and the Materials confirmed for the study's Study Type.
 */
export const purchasedArtifactsSchema = z.strictObject({
  rqs: purchasedArtifactSchema,
  dg: purchasedArtifactSchema,
  audience: purchasedArtifactSchema,
  goal: purchasedArtifactSchema,
  materials: purchasedArtifactSchema,
});
export type PurchasedArtifacts = z.infer<typeof purchasedArtifactsSchema>;

/** Safe for the authenticated Study owner; contains no checkout credentials. */
export const purchaseSchema = z.object({
  id: z.uuid(),
  studyId: z.uuid(),
  state: purchaseStateSchema,
  quote: issuedQuoteSchema,
  artifacts: purchasedArtifactsSchema,
  deploymentId: z.string().min(1),
  provider: z.enum(PAYMENT_PROVIDERS),
  providerMode: z.literal('sandbox'),
  launchMode: z.literal('simulation'),
  providerAccountId: z.string().min(1).nullable(),
  checkoutSessionId: z.string().min(1).nullable(),
  paymentIntentId: z.string().min(1).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime().nullable(),
  paidAt: z.iso.datetime().nullable(),
  closedAt: z.iso.datetime().nullable(),
});
export type Purchase = z.infer<typeof purchaseSchema>;

/** A purchase names the issued quote the researcher accepted, and nothing else about the price. */
export const createPurchaseSchema = z.strictObject({ quote_id: z.uuid() });
export type CreatePurchaseRequest = z.infer<typeof createPurchaseSchema>;

/**
 * Where a paid purchase's launch stands.
 *
 * - `queued` — the provisioning job is waiting for the worker; no run has begun.
 * - `running` — a run is under way.
 * - `retrying` — a step failed and the queue is coming back for it; `stop` says what failed.
 * - `stopped` — nothing will run the study again until somebody retries its setup; `stop` says
 *   why.
 * - `fielding` — the run finished and the study is in Remesh.
 */
export const PURCHASE_FULFILLMENT_STATES = [
  'queued',
  'running',
  'retrying',
  'stopped',
  'fielding',
] as const;
export type PurchaseFulfillmentState = (typeof PURCHASE_FULFILLMENT_STATES)[number];

export interface PurchaseFulfillment {
  state: PurchaseFulfillmentState;
  stop: { code: string; message: string } | null;
}

/**
 * A payment problem as the study's owner is shown it: what kind, when, and whether it is settled.
 * An unresolved one is a payment somebody has to act on.
 */
export interface PurchasePaymentProblem {
  kind: PaymentProblemKind;
  createdAt: string;
  resolved: boolean;
}

/**
 * Where a pending purchase's checkout stands, from what the purchase records:
 *
 * - `opening` — no checkout session is bound to it yet.
 * - `open` — a session is bound; whether it has been paid is the provider's to say.
 */
export type PurchaseCheckoutState = 'opening' | 'open';

/**
 * A study's payment as its owner reads it.
 *
 * - `unpaid` — no purchase holds the study: none was opened, or the latest one closed unpaid.
 * - `pending` — a purchase is open and holds the study's documents; `checkout` says whether its
 *   session is bound yet.
 * - `paid` — the purchase is paid; `fulfillment` says where its launch stands.
 *
 * `problems` lists the money facts about the study's purchases that no purchase state accounts
 * for: every unresolved one, and those resolved within `RESOLVED_PAYMENT_PROBLEM_LISTED_MS`,
 * newest first.
 */
export type StudyPurchaseStatus = { problems: PurchasePaymentProblem[] } & (
  | {
      kind: 'unpaid';
      purchase: Purchase | null;
      editingHeld: false;
      checkout: null;
      fulfillment: null;
    }
  | {
      kind: 'pending';
      purchase: Purchase;
      editingHeld: true;
      checkout: PurchaseCheckoutState;
      fulfillment: null;
    }
  | {
      kind: 'paid';
      purchase: Purchase;
      editingHeld: true;
      checkout: null;
      fulfillment: PurchaseFulfillment;
    }
);

/** Normalized server-side evidence, produced only after the provider has been verified. */
const providerIdentitySchema = z.object({
  purchaseId: z.uuid(),
  studyId: z.uuid(),
  deploymentId: z.string().min(1),
  provider: z.enum(PAYMENT_PROVIDERS),
  providerMode: z.literal('sandbox'),
  providerAccountId: z.string().min(1),
  checkoutSessionId: z.string().min(1),
  amountCents: z.int().nonnegative(),
  currency: z.literal('USD'),
});
export const verifiedPurchaseOutcomeSchema = z.discriminatedUnion('outcome', [
  providerIdentitySchema.extend({
    outcome: z.literal('paid'),
    paymentIntentId: z.string().min(1),
    confirmedAt: z.iso.datetime(),
  }),
  providerIdentitySchema.extend({
    outcome: z.enum(['cancelled', 'expired']),
    sessionStatus: z.literal('expired'),
    paymentStatus: z.literal('unpaid'),
    confirmedAt: z.iso.datetime(),
  }),
]);
export type VerifiedPurchaseOutcome = z.infer<typeof verifiedPurchaseOutcomeSchema>;

export const purchaseHoldsEditing = (state: PurchaseState): boolean =>
  state === 'pending' || state === 'paid';

/** Paid is monotonic; contradictory terminal evidence requires reconciliation. */
export const nextPurchaseState = (
  current: PurchaseState,
  outcome: VerifiedPurchaseOutcome['outcome'],
): PurchaseState => {
  if (current === 'paid') return 'paid';
  if (current === 'pending' || current === outcome) return outcome;
  if (outcome !== 'paid') return current;
  throw new DomainError('purchase_conflict', 'a closed unpaid purchase needs reconciliation');
};

export const assertPurchaseEvidenceMatches = (
  purchase: Purchase,
  evidence: VerifiedPurchaseOutcome,
): void => {
  if (
    purchase.id !== evidence.purchaseId ||
    purchase.studyId !== evidence.studyId ||
    purchase.deploymentId !== evidence.deploymentId ||
    purchase.provider !== evidence.provider ||
    purchase.providerMode !== evidence.providerMode ||
    purchase.providerAccountId !== evidence.providerAccountId ||
    purchase.checkoutSessionId !== evidence.checkoutSessionId ||
    purchase.quote.total_cents !== evidence.amountCents ||
    purchase.quote.currency !== evidence.currency ||
    (evidence.outcome === 'paid' &&
      purchase.paymentIntentId !== null &&
      purchase.paymentIntentId !== evidence.paymentIntentId)
  ) {
    throw new DomainError('purchase_conflict', 'provider evidence does not match this purchase');
  }
};

/**
 * When a purchase's checkout attempt ends: its `expires_at`, or an hour after it was opened for a
 * row written with none.
 */
export const attemptExpiresAt = (purchase: Pick<Purchase, 'expiresAt' | 'createdAt'>): Date =>
  new Date(
    purchase.expiresAt === null
      ? Date.parse(purchase.createdAt) + CHECKOUT_SESSION_LIFETIME_MS
      : Date.parse(purchase.expiresAt),
  );

/**
 * Whether an attempt is more than `UNOPENED_ATTEMPT_GRACE_MS` past its end at `now`, by when no
 * session for it can still be open.
 */
export const attemptPastGrace = (
  purchase: Pick<Purchase, 'expiresAt' | 'createdAt'>,
  now: Date,
): boolean => now.getTime() > attemptExpiresAt(purchase).getTime() + UNOPENED_ATTEMPT_GRACE_MS;
